import { Injectable } from '@angular/core';
import { AuthService } from '@auth/services';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  FileGenerated,
  ProjectSettingsChanged,
  ProjectSetupChanged,
  UserRightsChanged,
} from '@shared/models/signalr';
import { SignRequestReportUploaded } from '@shared/models/signalr/sign-request-report-uploaded.model';
import { AppFacade } from '@shared/state/facades';
import * as signalR from '@microsoft/signalr';
import { combineLatest, filter, Subject, tap } from 'rxjs';

@UntilDestroy()
@Injectable()
export class SignalRService {
  public projectSetupChanged$ = new Subject<ProjectSetupChanged>();
  public userRightsChanged$ = new Subject<UserRightsChanged>();
  public projectSettingsChanged$ = new Subject<ProjectSettingsChanged>();
  public fileGenerated$ = new Subject<FileGenerated>();
  public signRequestReportUploaded$ = new Subject<SignRequestReportUploaded>();

  private connection: signalR.HubConnection | null = null;
  private accessToken: string;
  private isAppOnline: boolean;

  constructor(private appFacade: AppFacade, private authService: AuthService) {
    combineLatest([
      this.appFacade.query.isAppOnline$,
      this.authService.authenticationResult$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([_isAppOnline, identity]) => !!identity),
        tap(([isAppOnline, identity]) => {
          this.isAppOnline = isAppOnline;
          this.accessToken = identity.accessToken;

          if (!isAppOnline) {
            return;
          }

          if (!this.connection) {
            this.initHub();
          }
        })
      )
      .subscribe();
  }

  disconnect(): void {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
    }
  }

  private initHub(): void {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.dprApi.endpoint}/hubs/notifications`, {
        accessTokenFactory: () => this.accessToken,
        withCredentials: false,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.registerMessageHandlers();
    this.registerConnectionEventHandlers();

    this.connection
      .start()
      .then(() => console.log('SignalR connected'))
      .catch((err) => console.error('SignalR connect error', err));
  }

  private registerConnectionEventHandlers(): void {
    this.connection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR reconnected', connectionId);
    });

    this.connection.onclose((error) => {
      console.log('SignalR disconnected', error);

      // withAutomaticReconnect exhausted its retries — manual fallback
      if (this.isAppOnline) {
        setTimeout(() => {
          this.connection
            ?.start()
            .then(() => console.log('SignalR reconnected'))
            .catch((err) => console.error('SignalR reconnect error', err));
        }, 5000);
      }
    });
  }

  private registerMessageHandlers(): void {
    this.connection.on('ProjectSetupChanged', (data: ProjectSetupChanged) => {
      this.projectSetupChanged$.next(data);
    });

    this.connection.on('UserRightsChanged', (data: UserRightsChanged) => {
      this.userRightsChanged$.next(data);
    });

    this.connection.on(
      'ProjectSettingsChanged',
      (data: ProjectSettingsChanged) => {
        this.projectSettingsChanged$.next(data);
      }
    );

    this.connection.on('FileGenerated', (data: FileGenerated) => {
      this.fileGenerated$.next(data);
    });

    this.connection.on(
      'SignRequestReportUploaded',
      (data: SignRequestReportUploaded) => {
        this.signRequestReportUploaded$.next(data);
      }
    );
  }
}
